import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  getCashbackIssuances,
  issueCashback,
  CashbackIssuanceListResponse,
  IssueCashbackPayload,
  IssueCashbackResponse,
} from "../../apis/cashback";
import { ListCouponsQuery } from "./types/couponListTypes";

interface CashbackState {
  issuances: CashbackIssuanceListResponse["data"] | null;
  /**
   * Unfiltered count of all cashback issuances, used by the Coupons page tab
   * badge. Kept SEPARATE from `issuances` so a tiny upfront count fetch
   * doesn't collide with the table's filtered list state.
   *
   * TODO: when backend adds `totalCashbackIssuances` to the coupons summary
   * endpoint, drop this field and the corresponding thunk and read from
   * `state.coupons.couponsSummary` instead (same pattern as the other tabs).
   */
  totalCount: number | null;
  loading: boolean;
  loadingCount: boolean;
  issuing: boolean;
  error: string | null;
}

const initialState: CashbackState = {
  issuances: null,
  totalCount: null,
  loading: false,
  loadingCount: false,
  issuing: false,
  error: null,
};

export const fetchCashbackIssuances = createAsyncThunk<
  CashbackIssuanceListResponse["data"],
  ListCouponsQuery | undefined,
  { rejectValue: string }
>("cashback/fetchIssuances", async (params, { rejectWithValue }) => {
  try {
    const res = await getCashbackIssuances(params ?? {});
    return res.data;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message ?? "Failed to load cashback issuances"
    );
  }
});

/**
 * Tiny count-only fetch (limit=1) used by the Coupons page to populate the
 * Cashback Issuances tab badge BEFORE the user opens that tab. Updates only
 * `totalCount` — never the `issuances` list — so it cannot overwrite the
 * table's filtered view if the fetches interleave.
 */
export const fetchCashbackIssuancesCount = createAsyncThunk<
  number,
  void,
  { rejectValue: string }
>("cashback/fetchIssuancesCount", async (_, { rejectWithValue }) => {
  try {
    const res = await getCashbackIssuances({ page: 1, limit: 1 });
    return res?.data?.meta?.totalItems ?? 0;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message ?? "Failed to load cashback count"
    );
  }
});

export const issueCashbackThunk = createAsyncThunk<
  IssueCashbackResponse,
  { buyerId: string } & IssueCashbackPayload,
  { rejectValue: string }
>(
  "cashback/issue",
  async ({ buyerId, ...payload }, { rejectWithValue }) => {
    try {
      const res = await issueCashback(buyerId, payload);
      return res;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ?? "Failed to issue cashback"
      );
    }
  }
);

const cashbackSlice = createSlice({
  name: "cashback",
  initialState,
  reducers: {
    resetCashbackState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCashbackIssuances.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchCashbackIssuances.fulfilled,
        (
          state,
          action: PayloadAction<CashbackIssuanceListResponse["data"]>
        ) => {
          state.loading = false;
          state.issuances = action.payload;
        }
      )
      .addCase(fetchCashbackIssuances.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? null;
      })
      .addCase(fetchCashbackIssuancesCount.pending, (state) => {
        state.loadingCount = true;
      })
      .addCase(
        fetchCashbackIssuancesCount.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.loadingCount = false;
          state.totalCount = action.payload;
        }
      )
      .addCase(fetchCashbackIssuancesCount.rejected, (state) => {
        state.loadingCount = false;
        // Soft-fail — keep the previous totalCount (might be stale, but
        // better than wiping to null on a transient network blip).
      })
      .addCase(issueCashbackThunk.pending, (state) => {
        state.issuing = true;
        state.error = null;
      })
      .addCase(issueCashbackThunk.fulfilled, (state) => {
        state.issuing = false;
      })
      .addCase(issueCashbackThunk.rejected, (state, action) => {
        state.issuing = false;
        state.error = (action.payload as string) ?? null;
      });
  },
});

export const { resetCashbackState } = cashbackSlice.actions;
export default cashbackSlice.reducer;
